import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { MODULE_CATALOG, BASE_MODULE_KEYS } from '../../config/moduleCatalog';

/**
 * Platform Operator Console — Organizations (event-platform spinoff).
 *
 * A 2-pane screen — left list (organization, # modules, # events, # users,
 * status), right read-only detail pane for the selected organization. The
 * action-heavy "Manage" experience (status / suspend / reactivate) lives in a
 * separate slide-over drawer, opened from the detail pane. Creating a new
 * organization uses its own drawer.
 *
 * Talks to GET/POST /platform/tenants and GET /platform/tenants/:id.
 */

interface TenantDomain {
  host: string;
  verified: boolean;
}
interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  enabledModules: string[];
  createdAt: string;
  hubUrl: string;
  customDomains: TenantDomain[];
  eventCount: number;
  userCount: number;
}
interface CreateTenantResult extends TenantSummary {
  adminEmail: string;
  tempPassword: string | null;
}

interface TenantUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  mustChangePassword: boolean;
  createdAt: string;
}
interface TenantDetail extends TenantSummary {
  users: TenantUser[];
}

/** Default new-tenant ceiling: every module the platform offers. */
const ALL_MODULE_KEYS = MODULE_CATALOG.map((m) => m.key);

function statusStyle(status: string): React.CSSProperties {
  if (status === 'Active') return { background: '#dcfce7', color: '#166534' };
  return { background: '#f1f5f9', color: '#475569' };
}

export function PlatformTenantsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Admin';
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  // Selected row → read-only detail pane (right).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Manage drawer (action-heavy) — separate experience.
  const [managing, setManaging] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  // Create drawer.
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [modules, setModules] = useState<string[]>([...ALL_MODULE_KEYS]);
  const [customDomain, setCustomDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateTenantResult | null>(null);

  async function loadTenants(selectFirst = false) {
    try {
      const data = await apiClient<TenantSummary[]>('/platform/tenants');
      setTenants(data);
      setError('');
      if (selectFirst && data.length > 0 && !selectedId) setSelectedId(data[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient<TenantSummary[]>('/platform/tenants');
        if (!cancelled) {
          setTenants(data);
          if (data.length > 0) setSelectedId(data[0].id);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load tenants');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Intent deep-link: /platform/organizations?new=1 opens the create drawer.
  useEffect(() => {
    if (searchParams.get('new') === '1' && isAdmin) {
      setShowCreate(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  // Load detail whenever selection changes.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    (async () => {
      try {
        const d = await apiClient<TenantDetail>(`/platform/tenants/${selectedId}`);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
          setDetailError(e instanceof Error ? e.message : 'Failed to load organization');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  async function changeStatus(next: 'Active' | 'Suspended') {
    if (!detail) return;
    setStatusBusy(true);
    setDetailError('');
    try {
      const updated = await apiClient<TenantDetail>(
        `/platform/tenants/${detail.id}/status`,
        { method: 'PATCH', body: { status: next } },
      );
      setDetail(updated);
      setTenants((prev) =>
        prev.map((t) => (t.id === updated.id ? { ...t, status: updated.status } : t)),
      );
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  }

  function onNameChange(v: string) {
    setName(v);
    if (!slugEdited) {
      setSlug(v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
    }
  }

  function toggleModule(m: string) {
    if (BASE_MODULE_KEYS.includes(m)) return; // base modules are always on
    setModules((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setJustCreated(null);
    try {
      const result = await apiClient<CreateTenantResult>('/platform/tenants', {
        method: 'POST',
        body: {
          name: name.trim(),
          slug: slug.trim(),
          adminEmail: adminEmail.trim(),
          modules,
          customDomain: customDomain.trim() || undefined,
        },
      });
      setJustCreated(result);
      setName('');
      setSlug('');
      setAdminEmail('');
      setCustomDomain('');
      setModules([...ALL_MODULE_KEYS]);
      setSlugEdited(false);
      setShowCreate(false);
      await loadTenants();
      setSelectedId(result.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  }

  const filtered = tenants.filter((t) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Organizations</h1>
          <p style={styles.sub}>Every organization workspace on the platform.</p>
        </div>
        <div style={styles.headingRight}>
          <input
            style={styles.search}
            placeholder="Search organization…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {isAdmin && (
            <button style={styles.newBtn} onClick={() => setShowCreate(true)}>
              + New
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {justCreated && (
        <div style={styles.success}>
          <strong>Created “{justCreated.name}”.</strong> Admin login:{' '}
          <code>{justCreated.adminEmail}</code>
          {justCreated.tempPassword && (
            <>
              {' '}
              / temp password <code>{justCreated.tempPassword}</code>
            </>
          )}
          . Reachable at <code>{justCreated.hubUrl}</code>.
        </div>
      )}

      {loading ? (
        <div style={styles.muted}>Loading organizations…</div>
      ) : tenants.length === 0 ? (
        <div style={styles.muted}>No organizations yet. Create your first one.</div>
      ) : (
        <div style={styles.twoPane} className="ep-org-twopane">
          {/* Left: summary list */}
          <div style={styles.listPane}>
            <table style={styles.table} className="ep-org-table">
              <thead>
                <tr>
                  <th style={styles.th}>Organization</th>
                  <th style={styles.thNum}>Mods</th>
                  <th style={styles.thNum}>Events</th>
                  <th style={styles.thNum}>Users</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={styles.emptyCell}>
                      No organizations match.
                    </td>
                  </tr>
                ) : (
                  filtered.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => setSelectedId(t.id)}
                      style={{ ...styles.row, ...(t.id === selectedId ? styles.rowActive : {}) }}
                    >
                      <td style={styles.td} data-label="Organization">
                        <div style={styles.tName}>{t.name}</div>
                        <div style={styles.tSlug}>{t.slug}</div>
                      </td>
                      <td style={styles.tdNum} data-label="Modules">
                        {t.enabledModules.length}
                      </td>
                      <td style={styles.tdNum} data-label="Events">
                        {t.eventCount}
                      </td>
                      <td style={styles.tdNum} data-label="Users">
                        {t.userCount}
                      </td>
                      <td style={styles.td} data-label="Status">
                        <span style={{ ...styles.pill, ...statusStyle(t.status) }}>{t.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Right: read-only detail pane */}
          <div style={styles.detailPane}>
            {detailLoading ? (
              <div style={styles.muted}>Loading…</div>
            ) : detailError ? (
              <div style={styles.error}>{detailError}</div>
            ) : !detail ? (
              <div style={styles.detailEmpty}>Select an organization to see its details.</div>
            ) : (
              <>
                <div style={styles.detailHead}>
                  <div>
                    <h2 style={styles.detailName}>{detail.name}</h2>
                    <div style={styles.detailSlug}>{detail.slug}</div>
                  </div>
                  <span style={{ ...styles.pill, ...statusStyle(detail.status) }}>{detail.status}</span>
                </div>

                <div style={styles.actionRow}>
                  {isAdmin && (
                    <button style={styles.manageBtn} onClick={() => setManaging(true)}>
                      Manage organization
                    </button>
                  )}
                  <Link to={`/platform/organizations/${detail.slug || detail.id}`} style={styles.fullPageLink}>
                    Open full page →
                  </Link>
                </div>

                {/* Quick stats */}
                <div style={styles.statGrid}>
                  <div style={styles.statCell}>
                    <div style={styles.statNum}>{detail.enabledModules.length}</div>
                    <div style={styles.statLabel}>Modules</div>
                  </div>
                  <div style={styles.statCell}>
                    <div style={styles.statNum}>{detail.eventCount}</div>
                    <div style={styles.statLabel}>Events</div>
                  </div>
                  <div style={styles.statCell}>
                    <div style={styles.statNum}>{detail.userCount}</div>
                    <div style={styles.statLabel}>Users</div>
                  </div>
                </div>

                {/* Address */}
                <div style={styles.block}>
                  <h3 style={styles.blockTitle}>Address</h3>
                  <div style={styles.addrRow}>
                    <code style={styles.addrCode}>{detail.hubUrl}</code>
                    <a
                      href={`http://${detail.hubUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        ...styles.openHubBtn,
                        ...(detail.status !== 'Active' ? styles.openHubDisabled : null),
                      }}
                      onClick={(e) => {
                        if (detail.status !== 'Active') e.preventDefault();
                      }}
                      aria-disabled={detail.status !== 'Active'}
                    >
                      Open hub ↗
                    </a>
                  </div>
                  {detail.customDomains.map((d) => (
                    <div key={d.host} style={styles.addrCustom}>
                      <code style={styles.addrCode}>{d.host}</code>{' '}
                      <span style={d.verified ? styles.verified : styles.unverified}>
                        {d.verified ? 'verified' : 'unverified'}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Modules */}
                <div style={styles.block}>
                  <h3 style={styles.blockTitle}>Modules ({detail.enabledModules.length})</h3>
                  <div style={styles.chipWrap}>
                    {detail.enabledModules.length === 0 ? (
                      <span style={styles.muted}>None enabled</span>
                    ) : (
                      detail.enabledModules.map((m) => (
                        <span key={m} style={styles.chip}>
                          {m}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Users */}
                <div style={styles.block}>
                  <h3 style={styles.blockTitle}>Users ({detail.users.length})</h3>
                  {detail.users.length === 0 ? (
                    <div style={styles.muted}>No users yet.</div>
                  ) : (
                    <table style={styles.userTable}>
                      <thead>
                        <tr>
                          <th style={styles.uth}>Name / Email</th>
                          <th style={styles.uth}>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.users.map((u) => (
                          <tr key={u.id}>
                            <td style={styles.utd}>
                              <div style={styles.uName}>{u.fullName}</div>
                              <div style={styles.uEmail}>{u.email}</div>
                            </td>
                            <td style={styles.utd}>
                              <span style={styles.roleChip}>{u.role}</span>
                              {u.mustChangePassword && <div style={styles.pwNote}>temp password</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Manage drawer — action-heavy experience, admin only */}
      {managing && detail && isAdmin && (
        <>
          <div style={styles.scrim} onClick={() => setManaging(false)} aria-hidden="true" />
          <aside style={styles.drawer} role="dialog" aria-label="Manage organization" aria-modal="true">
            <div style={styles.drawerHeader}>
              <div>
                <div style={styles.drawerTitle}>Manage: {detail.name}</div>
                <div style={styles.drawerSub}>{detail.slug}</div>
              </div>
              <button style={styles.drawerClose} onClick={() => setManaging(false)} aria-label="Close panel">
                ×
              </button>
            </div>
            <div style={styles.drawerBody}>
              {detailError && (
                <div style={styles.error} onClick={() => setDetailError('')}>
                  {detailError}
                </div>
              )}

              <div style={styles.sectionLabel}>Status</div>
              <div style={styles.statusRow}>
                <span style={{ ...styles.pill, ...statusStyle(detail.status) }}>{detail.status}</span>
                {detail.status === 'Active' ? (
                  <button style={styles.suspendBtn} onClick={() => changeStatus('Suspended')} disabled={statusBusy}>
                    {statusBusy ? 'Working…' : 'Suspend organization'}
                  </button>
                ) : (
                  <button style={styles.activateBtn} onClick={() => changeStatus('Active')} disabled={statusBusy}>
                    {statusBusy ? 'Working…' : 'Reactivate organization'}
                  </button>
                )}
              </div>
              {detail.status !== 'Active' && (
                <div style={styles.suspendNote}>
                  Suspended organizations cannot be reached — their hub address returns “no workspace
                  configured” and users can’t sign in.
                </div>
              )}

              <div style={styles.sectionLabel}>Hub address</div>
              <div style={styles.addrRow}>
                <code style={styles.addrCode}>{detail.hubUrl}</code>
                <a
                  href={`http://${detail.hubUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    ...styles.openHubBtn,
                    ...(detail.status !== 'Active' ? styles.openHubDisabled : null),
                  }}
                  onClick={(e) => {
                    if (detail.status !== 'Active') e.preventDefault();
                  }}
                >
                  Open hub ↗
                </a>
              </div>

              <div style={styles.sectionLabel}>Full page</div>
              <Link to={`/platform/organizations/${detail.slug || detail.id}`} style={styles.fullPageLink}>
                Open full organization page →
              </Link>
            </div>
          </aside>
        </>
      )}

      {/* Create drawer — admin only */}
      {showCreate && isAdmin && (
        <>
          <div style={styles.scrim} onClick={() => setShowCreate(false)} aria-hidden="true" />
          <aside style={styles.drawer} role="dialog" aria-label="New organization" aria-modal="true">
            <div style={styles.drawerHeader}>
              <div style={styles.drawerTitle}>New organization</div>
              <button style={styles.drawerClose} onClick={() => setShowCreate(false)} aria-label="Close panel">
                ×
              </button>
            </div>
            <div style={styles.drawerBody}>
              <form onSubmit={handleCreate}>
                <label style={styles.label}>Customer name</label>
                <input
                  style={styles.input}
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Acme Events Co."
                  required
                />

                <label style={styles.label}>Slug (hub subdomain)</label>
                <input
                  style={styles.input}
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugEdited(true);
                  }}
                  placeholder="acme"
                  pattern="[a-z0-9]([a-z0-9\-]*[a-z0-9])?"
                  required
                />
                {slug && <div style={styles.hint}>→ {slug}.{'<hub-domain>'}</div>}

                <label style={styles.label}>First admin email</label>
                <input
                  style={styles.input}
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="ops@acme.com"
                  required
                />

                <label style={styles.label}>Modules (organization ceiling)</label>
                <p style={styles.moduleHint}>
                  The set this organization may ever use. Base modules are always on.
                </p>
                <div style={styles.moduleList}>
                  {MODULE_CATALOG.map((m) => {
                    const on = modules.includes(m.key);
                    const base = !!m.isBase;
                    return (
                      <button
                        type="button"
                        key={m.key}
                        onClick={() => toggleModule(m.key)}
                        disabled={base}
                        aria-pressed={on}
                        style={{
                          ...styles.moduleItem,
                          ...(on ? styles.moduleItemOn : styles.moduleItemOff),
                          ...(base ? styles.moduleItemBase : null),
                        }}
                      >
                        <span style={styles.moduleIcon}>{m.icon}</span>
                        <span style={styles.moduleItemText}>
                          <span style={styles.moduleItemLabel}>{m.label}</span>
                          <span style={styles.moduleItemDesc}>{m.description}</span>
                        </span>
                        <span style={styles.moduleItemState}>
                          {base ? 'Base' : on ? '✓' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <label style={styles.label}>Custom domain (optional)</label>
                <input
                  style={styles.input}
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  placeholder="events.acme.com"
                />

                <button type="submit" style={styles.submit} disabled={submitting}>
                  {submitting ? 'Creating…' : 'Create organization'}
                </button>
              </form>
            </div>
          </aside>
        </>
      )}

      {/* Responsive: stack the panes and collapse the list to cards on mobile. */}
      <style>{`
        @media (max-width: 860px) {
          .ep-org-twopane { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 720px) {
          .ep-org-table thead { display: none; }
          .ep-org-table, .ep-org-table tbody, .ep-org-table tr, .ep-org-table td {
            display: block;
            width: 100%;
          }
          .ep-org-table tr {
            border-bottom: 8px solid #f1f5f9;
            padding: 6px 4px;
          }
          .ep-org-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            text-align: right;
            padding: 7px 14px;
          }
          .ep-org-table td::before {
            content: attr(data-label);
            font-weight: 700;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #94a3b8;
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
}

const INK = '#0b1120';
const ACCENT = '#4f46e5';
const ACCENT_SOFT = '#eef2ff';
const BORDER = '#e2e8f0';
const MUTED = '#64748b';

const styles: Record<string, React.CSSProperties> = {
  headingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' },
  headingRight: { display: 'flex', alignItems: 'center', gap: 12 },
  h1: { fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '0.25rem 0 0' },
  search: { padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', minWidth: '220px' },
  newBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' },

  muted: { color: '#94a3b8', fontSize: '0.9rem', padding: '1rem' },
  error: { color: '#b91c1c', background: '#fef2f2', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', cursor: 'pointer' },
  success: { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46', borderRadius: 8, padding: '10px 14px', marginBottom: 16 },

  twoPane: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: '1rem', alignItems: 'start' },
  listPane: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.6rem 0.9rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: MUTED, background: '#f8fafc', borderBottom: `1px solid ${BORDER}` },
  thNum: { textAlign: 'right', padding: '0.6rem 0.9rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: MUTED, background: '#f8fafc', borderBottom: `1px solid ${BORDER}` },
  row: { cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' },
  rowActive: { background: ACCENT_SOFT, boxShadow: `inset 3px 0 0 ${ACCENT}` },
  td: { padding: '0.6rem 0.9rem', fontSize: '0.85rem', color: '#334155', verticalAlign: 'middle' },
  tdNum: { padding: '0.6rem 0.9rem', fontSize: '0.85rem', color: '#334155', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' },
  emptyCell: { padding: '1.25rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' },
  tName: { fontWeight: 700, color: '#0f172a' },
  tSlug: { color: '#94a3b8', fontSize: '0.75rem', fontFamily: 'monospace', marginTop: 2 },
  pill: { display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 },

  // Detail pane
  detailPane: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '1.25rem', minHeight: '320px', position: 'sticky', top: '1rem' },
  detailEmpty: { color: '#94a3b8', fontSize: '0.9rem', padding: '2rem', textAlign: 'center' },
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' },
  detailName: { fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  detailSlug: { color: '#94a3b8', fontSize: '0.8rem', fontFamily: 'monospace', marginTop: 2 },
  actionRow: { display: 'flex', alignItems: 'center', gap: 14, margin: '14px 0 4px', flexWrap: 'wrap' },
  manageBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  fullPageLink: { color: ACCENT, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' },

  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, margin: '16px 0 4px' },
  statCell: { background: '#f8fafc', border: `1px solid #eef2f7`, borderRadius: 10, padding: '12px 8px', textAlign: 'center' },
  statNum: { fontSize: '1.4rem', fontWeight: 800, color: INK },
  statLabel: { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: MUTED, marginTop: 2 },

  block: { marginTop: '1rem', borderTop: '1px solid #f1f5f9', paddingTop: '1rem' },
  blockTitle: { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: MUTED, margin: '0 0 0.6rem', fontWeight: 700 },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  chip: { display: 'inline-block', background: ACCENT_SOFT, color: ACCENT, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', marginRight: 4, marginBottom: 2 },

  addrRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  addrCode: { background: '#f1f5f9', borderRadius: 6, padding: '3px 8px', fontSize: '0.85rem' },
  addrCustom: { marginTop: 6, fontSize: '0.82rem' },
  openHubBtn: { background: ACCENT, color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none' },
  openHubDisabled: { background: '#cbd5e1', cursor: 'not-allowed' },
  verified: { color: '#047857', fontWeight: 700 },
  unverified: { color: '#b45309', fontWeight: 700 },

  userTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  uth: { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' },
  utd: { padding: '10px 8px', borderBottom: '1px solid #f0f1f5', verticalAlign: 'top' },
  uName: { fontWeight: 600 },
  uEmail: { color: MUTED, fontSize: '0.78rem' },
  roleChip: { display: 'inline-block', background: ACCENT_SOFT, color: ACCENT, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700 },
  pwNote: { color: '#b45309', fontSize: '0.72rem', marginTop: 4 },

  // Drawer (manage + create)
  scrim: { position: 'fixed', inset: 0, background: 'rgba(11,17,32,0.45)', zIndex: 40 },
  drawer: { position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(460px, 100vw)', background: '#fff', boxShadow: '-8px 0 30px rgba(16,24,40,0.18)', zIndex: 50, display: 'flex', flexDirection: 'column' },
  drawerHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 22px', borderBottom: `1px solid ${BORDER}`, background: INK, color: '#fff' },
  drawerTitle: { fontWeight: 800, fontSize: '1.15rem' },
  drawerSub: { fontSize: '0.8rem', color: '#9aa4b2', fontFamily: 'monospace', marginTop: 2 },
  drawerClose: { background: 'transparent', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', lineHeight: 1, cursor: 'pointer', padding: 0 },
  drawerBody: { padding: '20px 22px', overflowY: 'auto', flex: 1 },

  sectionLabel: { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, margin: '20px 0 8px' },
  statusRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  suspendBtn: { background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  activateBtn: { background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  suspendNote: { marginTop: 10, fontSize: '0.8rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px' },

  // Create form controls
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', margin: '12px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.9rem' },
  hint: { fontSize: '0.75rem', color: MUTED, marginTop: 4, fontFamily: 'monospace' },
  moduleHint: { fontSize: '0.76rem', color: MUTED, margin: '0 0 8px' },
  moduleList: { display: 'flex', flexDirection: 'column', gap: 6 },
  moduleItem: { display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '9px 11px', borderRadius: 10, border: `1px solid ${BORDER}`, cursor: 'pointer', background: '#fff' },
  moduleItemOn: { borderColor: ACCENT, background: ACCENT_SOFT },
  moduleItemOff: { background: '#fff' },
  moduleItemBase: { cursor: 'default', opacity: 0.95 },
  moduleIcon: { fontSize: '1.1rem', lineHeight: 1 },
  moduleItemText: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  moduleItemLabel: { fontWeight: 700, fontSize: '0.84rem', color: INK },
  moduleItemDesc: { fontSize: '0.74rem', color: MUTED, marginTop: 1 },
  moduleItemState: { fontSize: '0.72rem', fontWeight: 700, color: ACCENT, whiteSpace: 'nowrap' },
  submit: { width: '100%', marginTop: 18, padding: '11px 0', background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer' },
};
